import { StateMachine } from "@/core/arch/state_machine";
import type { MoneyRepository } from "./money_repository";
import type { Payout, PayoutFilter } from "./types";
import { defaultPayoutFilter } from "./types";

const PAGE_SIZE = 50;
const DRAIN_LIMIT = 20;

export interface PayoutsListState {
  items: Payout[];
  status: "idle" | "loading" | "loaded" | "error";
  error?: string;
  filters: PayoutFilter;
  hasNext: boolean;
  hasPrev: boolean;
}

export class PayoutsListBloc extends StateMachine<PayoutsListState> {
  private _loadToken = 0;
  private _pageTokens: (string | undefined)[] = [undefined];
  private _nextToken: string | null = null;

  constructor(
    private repo: MoneyRepository,
    private businessId: string,
  ) {
    super({
      items: [],
      status: "idle",
      filters: defaultPayoutFilter(),
      hasNext: false,
      hasPrev: false,
    });
  }

  async load(): Promise<void> {
    this._pageTokens = [undefined];
    this._nextToken = null;
    await this._fetchCurrentPage();
  }

  async nextPage(): Promise<void> {
    if (this.state.status === "loading") return;
    if (!this.state.hasNext || this._nextToken == null) return;
    const tokensSnapshot = [...this._pageTokens];
    this._pageTokens.push(this._nextToken);
    const ok = await this._fetchCurrentPage();
    if (!ok) this._pageTokens = tokensSnapshot;
  }

  async prevPage(): Promise<void> {
    if (this.state.status === "loading") return;
    if (!this.state.hasPrev) return;
    const tokensSnapshot = [...this._pageTokens];
    this._pageTokens.pop();
    const ok = await this._fetchCurrentPage();
    if (!ok) this._pageTokens = tokensSnapshot;
  }

  async setFilter(patch: Partial<PayoutFilter>): Promise<void> {
    this.setState({
      ...this.state,
      filters: { ...this.state.filters, ...patch },
    });
    await this.load();
  }

  private async _fetchCurrentPage(): Promise<boolean> {
    const token = ++this._loadToken;
    this.setState({ ...this.state, status: "loading", error: undefined });
    const entryToken = this._pageTokens[this._pageTokens.length - 1];
    const filter = this.state.filters;

    const fetchPage = async (
      pageToken: string | undefined,
    ): Promise<{ items: Payout[]; nextToken: string | null }> => {
      const result = await this.repo.listPayouts({
        businessId: this.businessId,
        limit: PAGE_SIZE,
        nextToken: pageToken,
        filter,
      });
      return { items: result.items, nextToken: result.nextToken ?? null };
    };

    try {
      let page = await fetchPage(entryToken);
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return false;

      let drains = 0;
      while (
        page.items.length === 0 &&
        page.nextToken != null &&
        drains < DRAIN_LIMIT
      ) {
        page = await fetchPage(page.nextToken);
        // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
        if (token !== this._loadToken) return false;
        drains += 1;
      }

      this._nextToken = page.nextToken;
      this.setState({
        ...this.state,
        items: page.items,
        status: "loaded",
        error: undefined,
        hasNext: this._nextToken != null,
        hasPrev: this._pageTokens.length > 1,
      });
      return true;
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return false;
      this._nextToken = null;
      this.setState({
        ...this.state,
        status: "error",
        error: err instanceof Error ? err.message : "Failed to load payouts",
        hasNext: false,
        hasPrev: false,
      });
      return false;
    }
  }
}
